import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, FileText, User as UserIcon, AlertCircle, CheckCircle, Clock, XCircle, X, Plus, Mail, Phone, ChevronDown, Search } from 'lucide-react';
import { financialPowersAPI, vendorsAPI, departmentsAPI, currenciesAPI, vendorInvoicesAPI } from '../services/api';
import { Vendor, User, Currency } from '../types';
import { getCurrencySymbol, getCurrencyFontClass } from '../utils/currency';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import Spinner from '../components/Spinner';
import AttachmentViewer from '../components/AttachmentViewer';
import { decryptData } from '../utils/encryption';
import { useAuth } from '../contexts/AuthContext';

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

interface FinancialProposal {
  id: number;
  proposalCode?: string;
  vendor_name?: string;
  department_name?: string;
  invoice_type: string;
  amount: number;
  currency: string;
  created_at: string;
  status: string;
  vendor_id?: number;
  department_id?: number;
  approver_details?: any[];
  proposal_document_url?: string;
  proposal_document_filename?: string;
  proposal_document_size?: number;
  additional_attachments?: any[];
  approvers?: number[];
  vendor_email?: string;
  vendor_phone?: string;
  vendor_address?: string;
}

// const invoiceTypeOptions = [
//   { id: 'variable', label: 'Variable Payment' },
//   { id: 'fixed', label: 'Fixed Payment' },
// ];

const FinancialPowerProposalDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userPermissionsLocalStorage = localStorage.getItem('permissions') || '';
  const userPermissions = decryptData(userPermissionsLocalStorage);

  const [proposal, setProposal] = useState<FinancialProposal | null>(null);
  const [auditTrail, setAuditTrail] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSendingForApproval, setIsSendingForApproval] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [approvers, setApprovers] = useState<User[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  // const [departments, setDepartments] = useState<Department[]>([]);
  const proposalFileInputRef = useRef<HTMLInputElement>(null);
  const additionalAttachmentsInputRef = useRef<HTMLInputElement>(null);

  // Searchable dropdown states
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  // const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const departmentDropdownRef = useRef<HTMLDivElement>(null);
  // const [invoiceTypeDropdownOpen, setInvoiceTypeDropdownOpen] = useState(false);
  // const [invoiceTypeSearchTerm, setInvoiceTypeSearchTerm] = useState('');
  const invoiceTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [currencySearchTerm, setCurrencySearchTerm] = useState('');
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const [approverDropdownOpenIndex, setApproverDropdownOpenIndex] = useState<number | null>(null);
  const [approverSearchTerms, setApproverSearchTerms] = useState<string[]>(['']);
  const approverDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [editForm, setEditForm] = useState({
    department_id: '',
    vendor_id: '',
    invoice_type: '',
    currency: '',
    amount: '',
    proposalDocument: null as File | null,
    additional_attachments: [] as File[],
    approvers: [''] as string[],
    reference_name: '',
    initiator_note: '',
  });

  const [validationMessages, setValidationMessages] = useState<{
    approvers?: string;
  }>({});

  const [fieldErrors, setFieldErrors] = useState<{
    department_id?: string;
    vendor_id?: string;
    invoice_type?: string;
    currency?: string;
    amount?: string;
    reference_name?: string;
    initiator_note?: string;
  }>({});

  // Attachment viewer modal states
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState<string>('');
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string>('');

  useEffect(() => {
    const fetchProposalDetails = async () => {
      if (!id) {
        setError('Proposal ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch proposal details
        const proposalResponse = await financialPowersAPI.getById(parseInt(id));
        if (proposalResponse.success) {
          const proposalData = proposalResponse.data;
          setProposal(proposalData);

          // Initialize edit form when proposal is loaded
          setEditForm({
            department_id: proposalData.department_id?.toString() || '',
            vendor_id: proposalData.vendor_id?.toString() || '',
            invoice_type: proposalData.invoice_type || '',
            currency: proposalData.currency || '',
            amount: proposalData.amount?.toString() || '',
            proposalDocument: null,
            additional_attachments: [],
            approvers: proposalData.approvers && proposalData.approvers.length > 0
              ? proposalData.approvers.map((a: any) => a.toString())
              : [''],
            reference_name: proposalData.reference_name || '',
            initiator_note: proposalData.initiator_note || '',
          });

          setApproverSearchTerms(
            proposalData.approvers && proposalData.approvers.length > 0
              ? proposalData.approvers.map(() => '')
              : ['']
          );

          const currenciesResponse = await currenciesAPI.dropDown();
          if (currenciesResponse.success) {
            const currencyList = currenciesResponse.data || [];
            setCurrencies(currencyList);

            if (!proposalData.currency) {
              const defaultCurrency =
                currencyList.find((currency: Currency) => currency.is_default)?.name ||
                currencyList[0]?.name ||
                '';

              if (defaultCurrency) {
                setEditForm((prev) => ({ ...prev, currency: prev.currency || defaultCurrency }));
                setProposal((prev) => (prev ? { ...prev, currency: defaultCurrency } : prev));
              }
            }
          } else {
            setCurrencies([]);
          }
        } else {
          throw new Error(proposalResponse.message || 'Failed to fetch proposal details');
        }

        // Fetch audit trail
        const auditResponse = await financialPowersAPI.getAuditTrail(parseInt(id));
        if (auditResponse.success) {
          setAuditTrail(auditResponse.data || []);
        }

        // Fetch departments for dropdown
        const departmentsResponse = await departmentsAPI.dropDown();
        if (departmentsResponse.success) {
          // setDepartments(departmentsResponse.data);
        }

        // Fetch approvers for edit dropdown
        const approversResponse = await vendorInvoicesAPI.invoiceApproverDropdown();
        if (approversResponse.success) {
          setApprovers(approversResponse.data);
        }

      } catch (err: any) {
        console.error('Error fetching proposal details:', err);
        setError(err.message || 'Failed to load proposal details');
        errorModal('Failed to load proposal details');
      } finally {
        setLoading(false);
      }
    };

    fetchProposalDetails();
  }, [id]);

  // Fetch vendors when department changes
  useEffect(() => {
    const fetchVendors = async () => {
      if (!editForm.department_id) {
        setVendors([]);
        return;
      }

      try {
        const response = await vendorsAPI.allVendorsDropdown();
        if (response.success) {
          setVendors(response.data);
          // Clear vendor selection if current vendor is not in the filtered list
          if (editForm.vendor_id) {
            const vendorExists = response.data.some((v: Vendor) => v.id.toString() === editForm.vendor_id);
            if (!vendorExists) {
              setEditForm(prev => ({ ...prev, vendor_id: '' }));
            }
          }
        } else {
          setVendors([]);
          setEditForm(prev => ({ ...prev, vendor_id: '' }));
        }
      } catch (err) {
        setVendors([]);
        setEditForm(prev => ({ ...prev, vendor_id: '' }));
      }
    };
    fetchVendors();
  }, [editForm.department_id]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target as Node)) {
        setVendorDropdownOpen(false);
        setVendorSearchTerm('');
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setDepartmentDropdownOpen(false);
        // setDepartmentSearchTerm('');
      }
      if (invoiceTypeDropdownRef.current && !invoiceTypeDropdownRef.current.contains(event.target as Node)) {
        // setInvoiceTypeDropdownOpen(false);
        // setInvoiceTypeSearchTerm('');
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setCurrencyDropdownOpen(false);
        setCurrencySearchTerm('');
      }
      if (
        approverDropdownOpenIndex !== null &&
        approverDropdownRefs.current[approverDropdownOpenIndex] &&
        !approverDropdownRefs.current[approverDropdownOpenIndex]?.contains(event.target as Node)
      ) {
        setApproverDropdownOpenIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [approverDropdownOpenIndex]);

  const handleCancelEdit = () => {
    navigate('/financial-power-proposal');
    setEditForm({
      department_id: '',
      vendor_id: '',
      invoice_type: '',
      currency: '',
      amount: '',
      proposalDocument: null,
      additional_attachments: [],
      approvers: [''],
      reference_name: '',
      initiator_note: '',
    });
    if (proposalFileInputRef.current) {
      proposalFileInputRef.current.value = '';
    }
    if (additionalAttachmentsInputRef.current) {
      additionalAttachmentsInputRef.current.value = '';
    }
  };

  const validateFields = () => {
    const errors: typeof fieldErrors = {};

    // if (!editForm.department_id) {
    //   errors.department_id = 'Department is required';
    // }

    if (!editForm.vendor_id) {
      errors.vendor_id = 'Vendor is required';
    }

    // if (!editForm.invoice_type) {
    //   errors.invoice_type = 'Invoice type is required';
    // }

    // Validate currency - must be selected and exist in currencies list
    const currencyValue = editForm.currency?.trim() || '';
    if (!currencyValue) {
      errors.currency = 'Currency is required';
    } else {
      // If currencies are loaded, verify the selected currency exists in the list
      if (currencies.length > 0) {
        const currencyExists = currencies.some(c => c.name === currencyValue);
        if (!currencyExists) {
          errors.currency = 'Currency is required';
        }
      }
      // If currencies aren't loaded yet, we still require a non-empty value
      // The existence check will happen once currencies are loaded
    }

    const amountValue = parseFloat(editForm.amount);
    if (!editForm.amount || isNaN(amountValue) || amountValue <= 0) {
      errors.amount = 'Enter a valid amount';
    }

    if (!editForm.reference_name || !editForm.reference_name.trim()) {
      errors.reference_name = 'Reference name is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendForApproval = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!proposal) return;

    // Validate required fields first
    if (!validateFields()) {
      errorModal('Please fill in all required fields');
      return;
    }

    // Validate required approver fields for Send for Approval
    const approverIds = editForm.approvers.filter(id => Boolean(id));
    if (approverIds.length === 0) {
      setValidationMessages({ approvers: 'Select at least one approver' });
      errorModal('Please select at least one approver');
      return;
    }

    // Clear validation errors if all required fields are filled
    setValidationMessages({});

    setIsSendingForApproval(true);
    try {
      const formData = new FormData();
      // formData.append('department_id', editForm.department_id || '');
      formData.append('vendor_id', editForm.vendor_id);
      formData.append('invoice_type', "variable");
      formData.append('currency', editForm.currency || '');
      formData.append('amount', editForm.amount);
      formData.append('reference_name', editForm.reference_name || '');
      formData.append('initiator_note', editForm.initiator_note || '');
      // Append approvers as JSON array
      const approverIdsArray = approverIds.map(id => parseInt(id));
      formData.append('approvers', JSON.stringify(approverIdsArray));

      if (editForm.proposalDocument) {
        formData.append('proposal_document', editForm.proposalDocument);
      }

      if (editForm.additional_attachments && editForm.additional_attachments.length > 0) {
        editForm.additional_attachments.forEach((file) => {
          formData.append('additional_attachments', file);
        });
      }

      const response = await financialPowersAPI.update(proposal.id, formData);
      if (response.success) {
        toast.success('Financial approval sent for approval successfully');
        navigate('/financial-power-proposal');
        // Refresh the proposal data
        const updatedResponse = await financialPowersAPI.getById(parseInt(id!));
        if (updatedResponse.success) {
          setProposal(updatedResponse.data);
        }
      } else {
        errorModal(response.error || 'Error sending financial approval for approval. Please try again.');
      }
    } catch (error: any) {
      console.error('Error sending financial approval for approval:', error);
      errorModal(error?.response?.data?.error || 'Error sending financial approval for approval. Please try again.');
    } finally {
      setIsSendingForApproval(false);
    }
  };

  const handleAddApprover = () => {
    if (editForm.approvers.length >= 5) {
      return;
    }
    setEditForm((prev) => ({
      ...prev,
      approvers: [...prev.approvers, '']
    }));
    setApproverSearchTerms((prev) => [...prev, '']);
  };

  const handleRemoveApprover = (index: number) => {
    setEditForm((prev) => ({
      ...prev,
      approvers: prev.approvers.filter((_, i) => i !== index)
    }));
    setApproverSearchTerms((prev) => prev.filter((_, i) => i !== index));
    setApproverDropdownOpenIndex(null);
  };

  const handleApproverSelect = (index: number, value: string) => {
    setEditForm((prev) => {
      const nextApprovers = [...prev.approvers];
      nextApprovers[index] = value;
      return { ...prev, approvers: nextApprovers };
    });
    setApproverDropdownOpenIndex(null);
    setValidationMessages(prev => ({ ...prev, approvers: undefined }));
  };

  const filteredVendors = useMemo(() => {
    if (!vendorSearchTerm) return vendors;
    return vendors.filter((vendor) =>
      vendor.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())
    );
  }, [vendors, vendorSearchTerm]);

  const filteredCurrencies = useMemo(() => {
    if (!currencySearchTerm) return currencies;
    return currencies.filter((currency) =>
      currency.name.toLowerCase().includes(currencySearchTerm.toLowerCase())
    );
  }, [currencies, currencySearchTerm]);

  const getStatusIcon = (status: string) => {
    if (status === 'approved') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (status === 'rejected') {
      return <XCircle className="h-5 w-5 text-red-500" />;
    } else if (status?.startsWith('pending_approver_')) {
      return <Clock className="h-5 w-5 text-blue-500" />;
    } else {
      return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'approved') {
      return 'bg-green-100 text-green-800';
    } else if (status === 'rejected') {
      return 'bg-red-100 text-red-800';
    } else if (status?.startsWith('pending_approver_')) {
      return 'bg-blue-100 text-blue-800';
    } else {
      return 'bg-yellow-100 text-yellow-800';
    }
  };

  const currentCurrency = editForm.currency || proposal?.currency || '';
  const currencySymbol = getCurrencySymbol(currentCurrency || 'GBP');
  const currencyFontClass = getCurrencyFontClass(currentCurrency || 'GBP');

  // Check if user can edit
  const canEdit = (userPermissions === "admin") ||
    (userPermissions === "invoice_initiator" && proposal?.status === 'pending' && !proposal?.status?.startsWith('pending_approver_'));

  // Handle viewing attachment in modal
  const handleViewAttachment = (url: string, filename: string) => {
    if (!url) {
      errorModal('No attachment URL available');
      return;
    }
    setCurrentAttachmentUrl(url);
    setCurrentAttachmentName(filename);
    setShowAttachmentViewer(true);
  };

  const closeAttachmentViewer = () => {
    setShowAttachmentViewer(false);
    setCurrentAttachmentUrl('');
    setCurrentAttachmentName('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error || 'Financial approval not found'}</p>
          </div>
          <button
            onClick={() => navigate('/financial-power-proposal')}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Back to Financial Approvals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/financial-power-proposal')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Financial Approvals</span>
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-2xl font-bold text-gray-900">
            Financial Approval Details
          </h1>
        </div>
      </div>

      {/* Proposal Information */}
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Financial Approval Information</h2>
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(proposal.status)}`}>
                  {getStatusIcon(proposal.status)}
                  <span className="capitalize">{proposal.status}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                  <div ref={departmentDropdownRef} className="relative">
                    <button
                      type="button"
                      disabled={true}
                      onClick={() => {
                        setDepartmentDropdownOpen(!departmentDropdownOpen);
                      }}
                      className={`input-field w-full flex items-center justify-between text-left ${fieldErrors.department_id ? 'border-red-500' : ''}`}
                    >
                      <span className={editForm.department_id ? 'text-gray-900' : 'text-gray-500'}>
                        {user?.department_name || 'Select department'}
                        {/* {editForm.department_id
                          ? departments?.find(d => d.id.toString() === editForm.department_id)?.department || 'Select department'
                          : 'Select department'} */}
                      </span>
                      {/* <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${departmentDropdownOpen ? 'transform rotate-180' : ''}`} /> */}
                    </button>
                    {/* {departmentDropdownOpen && canEdit && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search departments..."
                              value={departmentSearchTerm}
                              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {departments && departments.length > 0 ? (
                            departments
                              .filter(dept =>
                                dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                              )
                              .map((dept) => (
                                <button
                                  key={dept.id}
                                  type="button"
                                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                  onClick={() => {
                                    setEditForm({ ...editForm, department_id: dept.id.toString(), vendor_id: '' });
                                    setDepartmentDropdownOpen(false);
                                    setDepartmentSearchTerm('');
                                    setFieldErrors(prev => ({ ...prev, department_id: undefined }));
                                  }}
                                >
                                  {dept.department}
                                </button>
                              ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No departments found
                            </div>
                          )}
                        </div>
                      </div>
                    )} */}
                  </div>
                  {fieldErrors.department_id && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.department_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <div ref={vendorDropdownRef} className="relative">
                    <button
                      type="button"
                      disabled={!editForm.department_id || !canEdit}
                      onClick={() => {
                        if (editForm.department_id && canEdit) {
                          setVendorDropdownOpen(!vendorDropdownOpen);
                        }
                      }}
                      className={`input-field w-full flex items-center justify-between text-left ${!editForm.department_id || !canEdit ? 'opacity-50 cursor-not-allowed' : ''} ${fieldErrors.vendor_id ? 'border-red-500' : ''}`}
                    >
                      <span className={editForm.vendor_id ? 'text-gray-900' : 'text-gray-500'}>
                        {!editForm.department_id
                          ? 'Select department first'
                          : editForm.vendor_id
                            ? filteredVendors?.find(v => v.id.toString() === editForm.vendor_id)?.name || 'Select vendor'
                            : 'Select vendor'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${vendorDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {vendorDropdownOpen && canEdit && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search vendors..."
                              value={vendorSearchTerm}
                              onChange={(e) => setVendorSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredVendors && filteredVendors.length > 0 ? (
                            filteredVendors
                              .filter(vendor => {
                                const vendorName = vendor?.name ?? '';
                                return vendorName
                                  .toString()
                                  .toLowerCase()
                                  .includes((vendorSearchTerm || '').toLowerCase());
                              })
                              .map((vendor) => (
                                <button
                                  key={vendor.id}
                                  type="button"
                                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                  onClick={() => {
                                    setEditForm({ ...editForm, vendor_id: vendor.id.toString() });
                                    setVendorDropdownOpen(false);
                                    setVendorSearchTerm('');
                                    setFieldErrors(prev => ({ ...prev, vendor_id: undefined }));
                                  }}
                                >
                                  {vendor.name}
                                </button>
                              ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No vendors found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {fieldErrors.vendor_id && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.vendor_id}</p>
                  )}
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Type *</label>
                  <div ref={invoiceTypeDropdownRef} className="relative">
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => {
                        if (canEdit) {
                          setInvoiceTypeDropdownOpen(!invoiceTypeDropdownOpen);
                        }
                      }}
                      className={`input-field w-full flex items-center justify-between text-left ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''} ${fieldErrors.invoice_type ? 'border-red-500' : ''}`}
                    >
                      <span className={editForm.invoice_type ? 'text-gray-900' : 'text-gray-500'}>
                        {editForm.invoice_type
                          ? invoiceTypeOptions.find(option => option.id === editForm.invoice_type)?.label
                          : 'Select Invoice Type'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${invoiceTypeDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {invoiceTypeDropdownOpen && canEdit && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search invoice types..."
                              value={invoiceTypeSearchTerm}
                              onChange={(e) => setInvoiceTypeSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {invoiceTypeOptions
                            .filter(option =>
                              option.label.toLowerCase().includes(invoiceTypeSearchTerm.toLowerCase())
                            )
                            .map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                onClick={() => {
                                  setEditForm({ ...editForm, invoice_type: option.id });
                                  setInvoiceTypeDropdownOpen(false);
                                  setInvoiceTypeSearchTerm('');
                                  setFieldErrors(prev => ({ ...prev, invoice_type: undefined }));
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {fieldErrors.invoice_type && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.invoice_type}</p>
                  )}
                </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency <span className="text-red-500">*</span>
                  </label>
                  <div ref={currencyDropdownRef} className="relative">
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => {
                        if (canEdit) {
                          setCurrencyDropdownOpen(!currencyDropdownOpen);
                        }
                      }}
                      className={`input-field w-full flex items-center justify-between text-left ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''} ${fieldErrors.currency ? 'border-red-500' : ''}`}
                    >
                      <span className={editForm.currency ? 'text-gray-900' : 'text-gray-500'}>
                        {(() => {
                          if (!editForm.currency || editForm.currency.trim() === '') return 'Select Currency';
                          const foundCurrency = filteredCurrencies.find(currency => currency.name === editForm.currency);
                          return foundCurrency ? foundCurrency.name : 'Select Currency';
                        })()}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${currencyDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {currencyDropdownOpen && canEdit && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search currencies..."
                              value={currencySearchTerm}
                              onChange={(e) => setCurrencySearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredCurrencies.map((currency) => (
                            <button
                              key={currency.id}
                              type="button"
                              className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                              onClick={() => {
                                setEditForm({ ...editForm, currency: currency.name });
                                setCurrencyDropdownOpen(false);
                                setCurrencySearchTerm('');
                                setFieldErrors(prev => ({ ...prev, currency: undefined }));
                              }}
                            >
                              {currency.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {fieldErrors.currency && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.currency}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                    <span className={`ml-1 text-xs text-gray-500 ${currencyFontClass}`}>({currencySymbol})</span>
                  </label>
                  <input
                    type="number"
                    // step="0.01"
                    required
                    disabled={!canEdit}
                    className={`input-field ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''} ${fieldErrors.amount ? 'border-red-500' : ''}`}
                    value={editForm.amount}
                    onChange={(e) => {
                      setEditForm({ ...editForm, amount: e.target.value });
                      setFieldErrors(prev => ({ ...prev, amount: undefined }));
                    }}
                    placeholder="Enter amount..."
                  />
                  {fieldErrors.amount && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.amount}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Vendor Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Vendor Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <UserIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Vendor Name</p>
                    <p className="font-medium text-gray-900">{proposal?.vendor_name || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Vendor Email</p>
                    <p className="font-medium text-gray-900">{proposal?.vendor_email || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Vendor Phone</p>
                    <p className="font-medium text-gray-900">{proposal?.vendor_phone || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Proposal Document */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Document</label>
            {proposal.proposal_document_url ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{proposal.proposal_document_filename || 'Proposal Document'}</p>
                    <p className="text-xs text-gray-500">
                      {proposal.proposal_document_size ? `${(proposal.proposal_document_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleViewAttachment(proposal.proposal_document_url || '', proposal.proposal_document_filename || 'Proposal Document')}
                  className="text-primary-600 hover:text-primary-800 p-1"
                  title="View file"
                >
                  <Eye className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No proposal document uploaded</div>
            )}
          </div>
        </div>

        {/* Proposal Initiation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Approval Initiation</h3>

          <div className="space-y-4">
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Reference Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={`input-field w-full ${fieldErrors.reference_name ? 'border-red-500' : ''}`}
                value={editForm.reference_name || ''}
                onChange={(e) => {
                  setEditForm({ ...editForm, reference_name: e.target.value });
                  setFieldErrors(prev => ({ ...prev, reference_name: undefined }));
                }}
                placeholder="Enter reference name..."
              />
              {fieldErrors.reference_name && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.reference_name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Initiator Notes</label>
              <textarea
                rows={3}
                className="input-field"
                value={editForm.initiator_note}
                onChange={(e) => setEditForm({ ...editForm, initiator_note: e.target.value })}
                placeholder="Enter initiator notes..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Approvers *</label>
              {canEdit && (
                <div className="mb-2">
                  <button
                    type="button"
                    onClick={handleAddApprover}
                    disabled={editForm.approvers.length >= 5}
                    className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Approver</span>
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {editForm.approvers.map((approverId, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div ref={(el) => (approverDropdownRefs.current[index] = el)} className="relative flex-1">
                      <button
                        type="button"
                        disabled={!canEdit}
                        onClick={() => {
                          if (canEdit) {
                            setApproverDropdownOpenIndex(approverDropdownOpenIndex === index ? null : index);
                          }
                        }}
                        className={`input-field w-full flex items-center justify-between text-left ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''} ${validationMessages.approvers ? 'border-red-500' : ''}`}
                      >
                        <span className={approverId ? 'text-gray-900' : 'text-gray-500'}>
                          {approverId
                            ? approvers.find(a => a.id.toString() === approverId)
                              ? `${approvers.find(a => a.id.toString() === approverId)?.first_name} ${approvers.find(a => a.id.toString() === approverId)?.last_name} (${approvers.find(a => a.id.toString() === approverId)?.email})`
                              : 'Select approver'
                            : 'Select approver'}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${approverDropdownOpenIndex === index ? 'transform rotate-180' : ''}`} />
                      </button>
                      {approverDropdownOpenIndex === index && canEdit && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b border-gray-200">
                            <div className="relative">
                              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Search approvers..."
                                value={approverSearchTerms[index] || ''}
                                onChange={(e) => {
                                  const newTerms = [...approverSearchTerms];
                                  newTerms[index] = e.target.value;
                                  setApproverSearchTerms(newTerms);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {approvers.filter((user) => {
                              const needle = (approverSearchTerms[index] ?? '').toLowerCase();
                              const haystack = `${user.first_name} ${user.last_name} ${user.email ?? ''}`.toLowerCase();
                              return haystack.includes(needle);
                            }).map((user) => (
                              <button
                                key={user.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                onClick={() => handleApproverSelect(index, user.id.toString())}
                              >
                                {user.first_name} {user.last_name} ({user.email})
                              </button>
                            ))}
                            {approvers.filter((user) => {
                              const needle = (approverSearchTerms[index] ?? '').toLowerCase();
                              const haystack = `${user.first_name} ${user.last_name} ${user.email ?? ''}`.toLowerCase();
                              return haystack.includes(needle);
                            }).length === 0 && (
                                <div className="px-4 py-2 text-sm text-gray-500 text-center">No approvers found</div>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                    {canEdit && editForm.approvers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveApprover(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Remove approver"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {validationMessages.approvers && (
                <p className="text-red-500 text-xs mt-1">{validationMessages.approvers}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Attachments</label>
              {canEdit && (
                <input
                  ref={additionalAttachmentsInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  className="input-field"
                  onChange={(e) => {
                    const newFiles = e.target.files ? Array.from(e.target.files) : [];
                    const oversizedFiles = newFiles.filter((file) => file.size > MAX_ATTACHMENT_SIZE_BYTES);
                    if (oversizedFiles.length > 0) {
                      const fileNames = oversizedFiles.map((file) => file.name).join(', ');
                      errorModal(
                        oversizedFiles.length === 1
                          ? `${fileNames} exceeds the 10MB limit. Please upload a file smaller than 10MB.`
                          : `${fileNames} exceed the 10MB limit. Please upload a file smaller than 10MB.`
                      );
                    }
                    const validNewFiles = newFiles.filter((file) => file.size <= MAX_ATTACHMENT_SIZE_BYTES);
                    const existingFiles = editForm.additional_attachments || [];
                    const combined = [...existingFiles, ...validNewFiles];
                    const deduped: File[] = [];
                    const seen = new Set<string>();
                    combined.forEach((f) => {
                      const key = `${f.name}-${f.size}-${(f as any).lastModified}`;
                      if (!seen.has(key)) {
                        seen.add(key);
                        deduped.push(f);
                      }
                    });
                    setEditForm({ ...editForm, additional_attachments: deduped });
                    if (additionalAttachmentsInputRef.current) {
                      additionalAttachmentsInputRef.current.value = '';
                    }
                  }}
                />
              )}
              <p className="text-xs text-gray-500 mt-1">You can select multiple files. Maximum file size is 10MB.</p>

              {/* Show existing additional attachments */}
              {proposal.additional_attachments && proposal.additional_attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Current Attachments:</h4>
                  <div className="space-y-2">
                    {proposal.additional_attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-primary-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                            <p className="text-xs text-gray-500">
                              {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Unknown size'} • {attachment.type}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => handleViewAttachment(attachment.url, attachment.filename)}
                            className="text-primary-600 hover:text-primary-800 p-1"
                            title="View file"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show newly uploaded files */}
              {editForm.additional_attachments && editForm.additional_attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">New Files to Upload:</h4>
                  <div className="space-y-2">
                    {editForm.additional_attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-200">
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-primary-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.name}</p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB • {file.type}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedFiles = editForm.additional_attachments.filter((_, i) => i !== index);
                            setEditForm({ ...editForm, additional_attachments: updatedFiles });
                          }}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remove file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {canEdit && (
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                  disabled={isSendingForApproval}
                >
                  <span>Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={handleSendForApproval}
                  disabled={isSendingForApproval}
                  className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingForApproval ? (
                    <>
                      <Spinner />
                      <span>Sending for Approval...</span>
                    </>
                  ) : (
                    <span>Send for Approval</span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Audit Trail */}
      {auditTrail.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Audit Trail</h3>
          <div className="space-y-4">
            {auditTrail.map((entry, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-primary-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">{entry.action}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(entry.created_at).toLocaleString()}
                    </p>
                  </div>
                  {entry.user && (
                    <p className="text-sm text-gray-600">By: {entry.user.name}</p>
                  )}
                  {entry.notes && (
                    <p className="text-sm text-gray-700 mt-1">{entry.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      <AttachmentViewer
        isOpen={showAttachmentViewer}
        url={currentAttachmentUrl}
        filename={currentAttachmentName}
        onClose={closeAttachmentViewer}
      />
    </div>
  );
};

export default FinancialPowerProposalDetails;

